import { ANALYTICS_INTERVAL_FILTERS } from '../../constants/analytics'
import { DATE_FORMATS } from '../../constants/date'
import { TRANSACTION_TYPES } from '../../constants/transactionTypes'
import { formatTimeToMoment, formatToIOSdate } from '../date.helpers'
import { addNumbers } from '../number.helpers'
import { getSortedKeysFromData } from '../object.helpers'
import { calculatePointValue } from '../token.helpers'

const buildExchangeActivityChartData = ({
  chartData,
  analyticsFilter: { interval }
}) => {
  const data = []
  const labels = []

  for (let time of getSortedKeysFromData(chartData)) {
    const chartRecord = chartData[time]
    data.push(calculateExchangeInOutCount(chartRecord))
    const momentTime = formatTimeToMoment(time)
    labels.push({
      formatted: buildFormattedChartLabel(momentTime, interval),
      unformatted: momentTime
    })
  }
  return {
    data,
    labels
  }
}

const buildTransactionsChartData = ({
  tokenProfile,
  chartData,
  analyticsFilter: { transactionType, interval }
}) => {
  const txCountData = {
    exchange: [],
    reward: [],
    redeem: []
  }
  const txTransferredData = []
  const txTransferredLabels = []
  for (let time of getSortedKeysFromData(chartData)) {
    const chartRecord = chartData[time]
    const x = formatToIOSdate(time)

    const momentTime = formatTimeToMoment(time)

    txTransferredLabels.push({
      formatted: buildFormattedChartLabel(momentTime, interval),
      unformatted: momentTime
    })
    txTransferredData.push({
      exchange: calculateExchangeInOutValue(chartRecord, tokenProfile),
      reward: calculatePointValue(
        chartRecord.rewardTxValue,
        tokenProfile.onramp
      ),
      redeem: calculatePointValue(
        chartRecord.redeemTxValue,
        tokenProfile.onramp
      )
    })

    txCountData.exchange.push({
      x,
      y: calculateExchangeInOutCount(chartRecord),
      interval
    })

    txCountData.reward.push({
      x,
      y: chartRecord?.rewardTxCount || 0,
      interval
    })
    txCountData.redeem.push({
      x,
      y: chartRecord?.redeemTxCount || 0,
      interval
    })
  }
  return {
    valueTransferredChart: {
      data: txTransferredData,
      filteredData: processPointsTransferredChartByFilter(
        txTransferredData,
        transactionType
      ),
      labels: txTransferredLabels
    },
    transactionsChart: {
      data: txCountData,
      filteredData: processTransactionsChartByFilter(
        txCountData,
        transactionType
      )
    }
  }
}

const processTransactionsChartByFilter = (data, transactionType) => {
  switch (transactionType) {
    case TRANSACTION_TYPES.EXCHANGE:
      return { exchange: data.exchange }
    case TRANSACTION_TYPES.REWARD:
      return { reward: data.reward }
    case TRANSACTION_TYPES.REDEEM:
      return { redeem: data.redeem }
    default:
      return {
        exchange: data.exchange,
        reward: data.reward,
        redeem: data.redeem
      }
  }
}

const processPointsTransferredChartByFilter = (data, transactionType) => {
  switch (transactionType) {
    case TRANSACTION_TYPES.EXCHANGE:
      return data.map((record) => record.exchange)
    case TRANSACTION_TYPES.REWARD:
      return data.map((record) => record.reward)
    case TRANSACTION_TYPES.REDEEM:
      return data.map((record) => record.redeem)
    default:
      return data.map(
        (record) => record.exchange + record.reward + record.redeem
      )
  }
}

const buildFormattedChartLabel = (momentTime, interval) => {
  switch (interval) {
    case ANALYTICS_INTERVAL_FILTERS.HOURLY:
      return momentTime.format(DATE_FORMATS.HOURLY)
    case ANALYTICS_INTERVAL_FILTERS.DAILY:
      return momentTime.format(DATE_FORMATS.DAILY)
    case ANALYTICS_INTERVAL_FILTERS.WEEKLY:
      return momentTime.format(DATE_FORMATS.WEEKLY)
    case ANALYTICS_INTERVAL_FILTERS.MONTHLY:
      return momentTime.format(DATE_FORMATS.MONTHLY)
    case ANALYTICS_INTERVAL_FILTERS.YEARLY:
      return momentTime.format(DATE_FORMATS.YEARLY)
  }
}

const buildTransactionsChartColors = ({ palette }, dataKeys, colorConfig) => {
  return dataKeys.map((dataKey) => palette[colorConfig[dataKey]].main)
}

const calculateExchangeInOutValue = (data, { offramp, onramp }) => {
  let valueExchanged = 0
  if (data?.exchangeOutTxValue) {
    valueExchanged += calculatePointValue(data.exchangeOutTxValue, offramp)
  }
  if (data?.exchangeInTxValue) {
    valueExchanged += calculatePointValue(data.exchangeInTxValue, onramp)
  }
  return valueExchanged
}

const calculateExchangeInOutCount = (data) => {
  return addNumbers(data?.exchangeOutTxCount, data?.exchangeInTxCount)
}

export {
  buildExchangeActivityChartData,
  buildTransactionsChartData,
  buildTransactionsChartColors,
  calculateExchangeInOutValue,
  calculateExchangeInOutCount,
  processTransactionsChartByFilter,
  processPointsTransferredChartByFilter,
  buildFormattedChartLabel
}
